// components/common/SearchBar.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { searchApi } from '../../api/search';
import CustomerLayout from '../layout/CustomerLayout';
import { useCategories } from '../../hooks/useCategories';

interface SearchBarProps {
  variant?: 'desktop' | 'mobile';
  onClose?: () => void;
  autoFocus?: boolean;
  className?: string;
  showCategoryFilter?: boolean;
  onCategoryChange?: (category: string) => void;
  initialCategory?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  variant = 'desktop',
  onClose,
  autoFocus = false,
  className = '',
  showCategoryFilter = true,
  onCategoryChange,
  initialCategory = ''
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Array<{ id: number; title: string }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const selectRef = useRef<HTMLSelectElement>(null);
  const navigate = useNavigate();
  const { categories, loading, isError } = useCategories();

  // Get current search params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlQuery = params.get('q') || '';
    const urlCategory = params.get('category') || '';

    setSearchQuery(urlQuery);
    setSelectedCategory(urlCategory);
  }, [location.search]);

  useEffect(() => {
    if (selectRef.current) {
      if (isCategoryOpen || selectedCategory) {
        selectRef.current.style.width = 'auto';
        // Calculate width based on content
        const tempSpan = document.createElement('span');
        tempSpan.style.visibility = 'hidden';
        tempSpan.style.whiteSpace = 'nowrap';
        tempSpan.style.font = window.getComputedStyle(selectRef.current).font;
        tempSpan.textContent = selectedCategory || 'All';
        document.body.appendChild(tempSpan);
        const width = tempSpan.offsetWidth + 40; // 40px for padding and arrow
        selectRef.current.style.width = `${Math.min(width, 200)}px`;
        document.body.removeChild(tempSpan);
      } else {
        selectRef.current.style.width = '80px';
      }
    }
  }, [isCategoryOpen, selectedCategory]);
  // Fetch suggestions on query change
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timer = setTimeout(() => {
        fetchSuggestions();
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery]);

  const fetchSuggestions = async () => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    try {
      const results = await searchApi.quickSearch(searchQuery, 5);
      setSuggestions(results.map(p => ({
        id: p.id,
        title: p.title
      })));
      setShowSuggestions(true);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!searchQuery.trim()) {
      // If no search query but category selected, navigate to products with category
      if (selectedCategory) {
        navigate(`/products?category=${selectedCategory}&sort=popular`);
      }
      return;
    } else {
      const params = new URLSearchParams();
      params.set('q', searchQuery.trim());
      if (selectedCategory) {
        params.set('category', selectedCategory);
      }

      navigate(`/search?${params.toString()}`);
      setShowSuggestions(false);
      if (onClose) onClose();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);

    // Immediately search with the suggestion
    const params = new URLSearchParams();
    params.set('q', suggestion);
    if (selectedCategory) {
      params.set('category', selectedCategory);
    }

    navigate(`/search?${params.toString()}`);
    if (onClose) onClose();
  };
  const handleClearFilters = () => {
    if (onCategoryChange) {
      onCategoryChange('');
    }
    setSearchQuery('');
    setShowSuggestions(false);
  };

  const applySearchFilters = () => {
    // This would be for a separate filter panel
    if (searchQuery.trim()) {
      handleSubmit(new Event('submit') as any);
    }
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);

    // If there's a search query, perform search with new category
    if (searchQuery.trim()) {
      const params = new URLSearchParams();
      params.set('q', searchQuery);
      if (category) {
        params.set('category', category);
      }
      navigate(`/search?${params.toString()}`);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedCategory('');
    if (location.pathname === '/search') {
      navigate('/products');
    }
  };

  // Desktop variant - CSS-only solution
  if (variant === 'desktop') {
    return (
      <div className={`relative ${className}`}>
        <form onSubmit={handleSubmit} className="relative w-full">
          <div className="relative flex items-stretch bg-gray-50 border border-gray-300 rounded-xl focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all">
            {/* Category dropdown - LEFT side */}
            {showCategoryFilter && (
              <div className="relative shrink-0">
                <select
                  ref={selectRef}
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  onFocus={() => setIsCategoryOpen(true)}
                  onBlur={() => setIsCategoryOpen(false)}
                  disabled={loading}
                  className="h-full pl-3 pr-5 py-2.5 bg-transparent text-sm text-gray-600 border-r border-gray-300 rounded-l-xl focus:outline-none focus:border-blue-500 appearance-none transition-all duration-200 min-w-[80px] max-w-[200px]"
                >
                  <option value="">All</option>
                  {loading ? (
                    <option disabled>Loading categories...</option>
                  ) : isError ? (
                    <option disabled>Failed to load categories</option>
                  ) : (
                    categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))
                  )}
                </select>
                <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            )}

            {/* Search input */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="search"
                placeholder="Search for laptops, phones, accessories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="w-full pl-10 pr-4 py-2.5 bg-transparent focus:outline-none placeholder:text-gray-500"
                aria-label="Search products"
                style={{
                  paddingLeft: showCategoryFilter ? '2.5rem' : '2.5rem',
                  paddingRight: '5rem'
                }}
              />
            </div>

            {/* Search button - RIGHT side */}
            <button
              type="submit"
              className="px-4 bg-blue-600 text-white font-medium rounded-r-xl hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              Search
            </button>
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
              <div className="py-1">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-b">
                  Popular matches
                </div>
                {suggestions.map(suggestion => (
                  <button
                    key={suggestion.id}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion.title)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-2"
                  >
                    <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{suggestion.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {isLoading && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            </div>
          )}
        </form>
      </div>
    );
  }

  // Mobile variant
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {onClose && (
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          aria-label="Close search"
          type="button"
        >
          <X className="h-5 w-5 text-gray-700" />
        </button>
      )}

      <form onSubmit={handleSubmit} className="flex-1 relative">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="search"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (e.target.value.length >= 2) {
                setShowSuggestions(true);
              }
            }}
            onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="w-full pl-10 pr-16 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
            autoFocus={autoFocus}
          />

          {/* Search button on mobile - ALWAYS VISIBLE */}
          <button
            type="submit"
            className="absolute inset-y-0 right-0 px-4 bg-blue-600 text-white font-medium rounded-r-xl hover:bg-blue-700 transition-colors"
          >
            Go
          </button>
        </div>

        {/* Mobile suggestions */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <div className="py-1">
              {suggestions.map(suggestion => (
                <button
                  key={suggestion.id}
                  type="button"
                  onClick={() => {
                    setSearchQuery(suggestion.title);
                    setShowSuggestions(false);
                    // Trigger search immediately
                    const params = new URLSearchParams();
                    params.set('q', suggestion.title);
                    if (selectedCategory) {
                      params.set('category', selectedCategory);
                    }
                    navigate(`/search?${params.toString()}`);
                    if (onClose) onClose();
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-2"
                >
                  <Search className="h-4 w-4 text-gray-400" />
                  {suggestion.title}
                </button>
              ))}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default SearchBar;